<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'change']);

const store = useStore();

onMounted(() => {
  store.dispatch('attributes/get');
});

const contactAttrs = useMapGetter('attributes/getContactAttributes');
const conversationAttrs = useMapGetter('attributes/getConversationAttributes');

const flowDatasources = inject('flowDatasources', ref([]));

const open = ref(false);
const search = ref('');
const activeGroup = ref('system-lead');
const dsField = ref('');
const selectedDs = ref(null);
const manualDsName = ref('');

const GROUPS = [
  { key: 'system-lead', label: 'Dados do Lead' },
  { key: 'system-business', label: 'Negócio' },
  { key: 'system-product', label: 'Produto' },
  { key: 'system-conversation', label: 'Conversa' },
  { key: 'additional-field-lead', label: 'Campo adicional - Lead' },
  { key: 'additional-field-conversation', label: 'Campo adicional - Conversa' },
  { key: 'system-system', label: 'Sistema' },
  { key: 'datasource', label: 'Fonte de dados' },
];

const SYSTEM_VARS = {
  'system-lead': [
    { id: 'cpfMascarado', label: 'CPF Mascarado', type: 'string', highlight: true },
    { id: 'leadId', label: 'ID do lead', type: 'id' },
    { id: 'leadName', label: 'Nome do lead', type: 'string' },
    { id: 'leadFirstName', label: 'Primeiro nome', type: 'string' },
    { id: 'leadEmail', label: 'Email', type: 'string' },
    { id: 'leadPhone', label: 'Telefone', type: 'string' },
    { id: 'leadTaxId', label: 'CPF/CNPJ', type: 'string' },
    { id: 'leadCompany', label: 'Empresa', type: 'string' },
    { id: 'leadCity', label: 'Cidade', type: 'string' },
    { id: 'leadState', label: 'Estado', type: 'string' },
    { id: 'leadAddress', label: 'Endereço', type: 'string' },
    { id: 'leadZip', label: 'CEP', type: 'string' },
    { id: 'leadNumber', label: 'Número', type: 'string' },
    { id: 'leadComplement', label: 'Complemento', type: 'string' },
    { id: 'leadBlock', label: 'Bairro', type: 'string' },
    { id: 'leadSource', label: 'Origem', type: 'string' },
    { id: 'leadSourceId', label: 'ID da origem', type: 'string' },
    { id: 'leadSourceUrl', label: 'URL da origem', type: 'string' },
    { id: 'leadCtwaId', label: 'CTWA ID', type: 'string' },
    { id: 'leadSite', label: 'Site', type: 'string' },
    { id: 'leadNotes', label: 'Observações', type: 'string' },
    { id: 'leadAttendant', label: 'Atendente do lead', type: 'string' },
    { id: 'instagramName', label: 'Nome Instagram', type: 'string' },
    { id: 'birthDate', label: 'Data de nascimento', type: 'date' },
  ],
  'system-business': [
    { id: 'businessId', label: 'ID do negócio', type: 'id' },
    { id: 'businessCode', label: 'Código do negócio', type: 'number' },
    { id: 'businessTotal', label: 'Valor total', type: 'number' },
    { id: 'businessExternalId', label: 'ID externo', type: 'string' },
    { id: 'businessAttendant', label: 'Atendente do negócio', type: 'string' },
    { id: 'JSONbusinessProduct', label: 'Produtos (JSON)', type: 'string' },
  ],
  'system-product': [
    { id: 'productName', label: 'Nome do produto', type: 'string' },
    { id: 'productSku', label: 'SKU do produto', type: 'string' },
    { id: 'productPrice', label: 'Preço do produto', type: 'number' },
  ],
  'system-conversation': [
    { id: 'conversationId', label: 'ID da conversa', type: 'id' },
    { id: 'lastInput', label: 'Última resposta', type: 'string' },
    { id: 'chatAttendant', label: 'Atendente do chat', type: 'string' },
    { id: 'threadCode', label: 'Código da thread', type: 'number' },
    { id: 'department', label: 'Departamento', type: 'string' },
    { id: 'lastAiText', label: 'Última resposta IA', type: 'string' },
    { id: 'aiConsumedTokens', label: 'Tokens consumidos IA', type: 'string' },
  ],
  'system-system': [
    { id: 'currentDate', label: 'Data atual', type: 'date' },
    { id: 'companyName', label: 'Nome da empresa', type: 'string' },
    { id: 'companyEmail', label: 'Email da empresa', type: 'string' },
    { id: 'lastErrorCode', label: 'Código do último erro', type: 'string' },
    { id: 'lastErrorMessage', label: 'Mensagem do último erro', type: 'string' },
  ],
};

const TYPE_ICONS = {
  id: '#',
  string: 'Aa',
  number: '123',
  date: '',
  currency: 'R$',
};

const DS_COLORS = {
  teal: { bg: 'rgba(45, 212, 191, 0.15)', text: '#14b8a6', border: '#14b8a6' },
  blue: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6', border: '#3b82f6' },
  purple: { bg: 'rgba(168, 85, 247, 0.15)', text: '#a855f7', border: '#a855f7' },
  orange: { bg: 'rgba(249, 115, 22, 0.15)', text: '#f97316', border: '#f97316' },
  pink: { bg: 'rgba(236, 72, 153, 0.15)', text: '#ec4899', border: '#ec4899' },
  green: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e', border: '#22c55e' },
};

const additionalFieldVars = computed(() => {
  if (activeGroup.value === 'additional-field-lead') {
    return (contactAttrs.value || []).map(attr => ({
      id: `additional-field[${attr.attributeDisplayName}]`,
      label: attr.attributeDisplayName,
      type: attr.attributeDisplayType || 'string',
    }));
  }
  if (activeGroup.value === 'additional-field-conversation') {
    return (conversationAttrs.value || []).map(attr => ({
      id: `additional-field[${attr.attributeDisplayName}]`,
      label: attr.attributeDisplayName,
      type: attr.attributeDisplayType || 'string',
    }));
  }
  return [];
});

const isAdditionalField = computed(() =>
  activeGroup.value.startsWith('additional-field')
);
const isDatasource = computed(() => activeGroup.value === 'datasource');

const currentVars = computed(() => {
  if (isAdditionalField.value) return additionalFieldVars.value;
  if (isDatasource.value) return [];
  return SYSTEM_VARS[activeGroup.value] || [];
});

const filteredVars = computed(() => {
  const list = currentVars.value;
  const q = search.value.toLowerCase();
  if (!q) return list;
  return list.filter(v => v.label.toLowerCase().includes(q) || v.id.toLowerCase().includes(q));
});

const insert = (variable) => {
  const token = `{${variable.label}|${variable.id}}`;
  emit('update:modelValue', (props.modelValue || '') + token);
  emit('change');
  open.value = false;
  search.value = '';
};

const selectDatasource = (ds) => {
  selectedDs.value = ds;
  dsField.value = '';
};

const insertDatasource = () => {
  if (!selectedDs.value || !dsField.value) return;
  const name = selectedDs.value.name;
  const field = dsField.value;
  emit('update:modelValue', (props.modelValue || '') + `{${field}|[${name}]${field}}`);
  emit('change');
  open.value = false;
  dsField.value = '';
  selectedDs.value = null;
};

const insertDatasourceManual = () => {
  const field = dsField.value || 'campo';
  const name = manualDsName.value || 'Api-request-1';
  emit('update:modelValue', (props.modelValue || '') + `{${field}|[${name}]${field}}`);
  emit('change');
  open.value = false;
  dsField.value = '';
};

const toggle = () => {
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    activeGroup.value = 'system-lead';
    selectedDs.value = null;
    dsField.value = '';
  }
};

const close = () => {
  open.value = false;
};

const getDsColor = (color) => DS_COLORS[color] || DS_COLORS.teal;
</script>

<template>
  <div class="vp-container">
    <button
      type="button"
      class="vp-trigger"
      title="Inserir variável"
      @click.stop="toggle"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1" />
        <path d="M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1" />
      </svg>
    </button>

    <teleport to="body">
      <div v-if="open" class="vp-overlay" @click="close" />
      <div v-if="open" class="vp-popup">
        <!-- Search -->
        <div class="vp-search-wrap">
          <svg class="vp-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input v-model="search" type="text" class="vp-search" placeholder="Buscar variável..." />
          <button type="button" class="vp-close" @click="close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div class="vp-body">
          <!-- Groups (w-2/5) -->
          <div class="vp-groups">
            <button
              v-for="group in GROUPS"
              :key="group.key"
              type="button"
              class="vp-group-btn"
              :class="{ 'vp-group-active': activeGroup === group.key }"
              @click="activeGroup = group.key; selectedDs = null"
            >
              {{ group.label }}
            </button>
          </div>

          <!-- Variables (w-3/5) -->
          <div class="vp-vars">
            <!-- System + Additional field variables -->
            <template v-if="!isDatasource">
              <button
                v-for="v in filteredVars"
                :key="v.id"
                type="button"
                class="vp-var-item"
                :class="{ 'vp-var-highlight': v.highlight }"
                @click="insert(v)"
              >
                <span class="vp-var-type-badge" :class="{ 'vp-badge-highlight': v.highlight }">{{ TYPE_ICONS[v.type] || 'Aa' }}</span>
                <span class="vp-var-label">{{ v.label }}</span>
              </button>
              <p v-if="!filteredVars.length && isAdditionalField" class="vp-empty">
                Nenhum campo adicional cadastrado.<br>
                Vá em Configurações > Campos personalizados para criar.
              </p>
              <p v-else-if="!filteredVars.length" class="vp-empty">Nenhuma variável encontrada</p>
            </template>

            <!-- Datasource -->
            <template v-if="isDatasource">
              <!-- Step 1: Show existing datasources as clickable list -->
              <template v-if="!selectedDs">
                <div v-if="flowDatasources.length" class="vp-ds-list">
                  <p class="vp-ds-hint">Selecione a fonte de dados do fluxo:</p>
                  <button
                    v-for="ds in flowDatasources"
                    :key="ds.name"
                    type="button"
                    class="vp-ds-item"
                    :style="{
                      background: getDsColor(ds.color).bg,
                      borderColor: getDsColor(ds.color).border,
                    }"
                    @click="selectDatasource(ds)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :style="{ color: getDsColor(ds.color).text }">
                      <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>
                    </svg>
                    <span class="vp-ds-name" :style="{ color: getDsColor(ds.color).text }">{{ ds.name }}</span>
                  </button>
                </div>
                <div v-else class="vp-ds-empty">
                  <p class="vp-ds-hint">Nenhuma fonte de dados encontrada neste fluxo.</p>
                  <p class="vp-ds-hint">Adicione um bloco de API para criar fontes de dados.</p>
                </div>
                <!-- Manual entry fallback -->
                <div class="vp-ds-manual">
                  <p class="vp-ds-manual-title">Ou digite manualmente:</p>
                  <div class="vp-ds-manual-row">
                    <input v-model="manualDsName" type="text" class="vp-field-input" placeholder="Api-request-1" />
                    <input v-model="dsField" type="text" class="vp-field-input" placeholder="campo" />
                    <button type="button" class="vp-ds-insert-btn" @click="insertDatasourceManual">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                    </button>
                  </div>
                </div>
              </template>

              <!-- Step 2: Selected datasource — enter field path -->
              <template v-else>
                <div class="vp-ds-selected">
                  <div class="vp-ds-selected-header">
                    <button type="button" class="vp-ds-back" @click="selectedDs = null">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    <span class="vp-ds-selected-name" :style="{ color: getDsColor(selectedDs.color).text }">{{ selectedDs.name }}</span>
                  </div>
                  <label class="vp-field-label">Campo do retorno</label>
                  <input
                    v-model="dsField"
                    type="text"
                    class="vp-field-input"
                    placeholder="Ex: data.access_token"
                    @keyup.enter="insertDatasource"
                  />
                  <p class="vp-field-preview">
                    <code>{{"{"}}{{ dsField || 'campo' }}|[{{ selectedDs.name }}]{{ dsField || 'campo' }}{{"}"}}</code>
                  </p>
                  <button type="button" class="vp-field-insert" :disabled="!dsField" @click="insertDatasource">
                    Inserir variável
                  </button>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.vp-container {
  position: relative;
  display: inline-flex;
}

.vp-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgb(var(--blue-9));
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.vp-trigger:hover {
  background: rgb(var(--blue-10));
  transform: scale(1.05);
}

.vp-overlay {
  position: fixed;
  inset: 0;
  z-index: 999;
}

.vp-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  width: 34.37rem;
  height: 21.5rem;
  max-height: 21.5rem;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: vp-in 0.15s ease;
}

@keyframes vp-in {
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

.vp-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid rgb(var(--slate-4));
  position: relative;
}

.vp-search-icon {
  position: absolute;
  left: 1.25rem;
  color: rgb(var(--slate-8));
  pointer-events: none;
}

.vp-search {
  flex: 1;
  padding: 0.375rem 0.625rem 0.375rem 2rem;
  font-size: 13px;
  color: rgb(var(--slate-12));
  background: rgb(var(--alpha-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  outline: none;
}
.vp-search:focus { border-color: rgb(var(--blue-9)); }
.vp-search::placeholder { color: rgb(var(--slate-9)); }

.vp-close {
  padding: 0.25rem;
  border-radius: 6px;
  color: rgb(var(--slate-9));
  transition: all 0.12s ease;
  flex-shrink: 0;
}
.vp-close:hover {
  color: rgb(var(--slate-12));
  background: rgba(var(--alpha-2));
}

.vp-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.vp-groups {
  width: 40%;
  border-right: 1px solid rgb(var(--slate-4));
  padding: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow-y: auto;
  flex-shrink: 0;
}

.vp-group-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--slate-10));
  cursor: pointer;
  transition: all 0.1s ease;
  text-align: left;
  width: 100%;
  white-space: nowrap;
}
.vp-group-btn:hover {
  background: rgba(var(--alpha-1));
  color: rgb(var(--slate-12));
}
.vp-group-active {
  background: rgba(var(--blue-4), 0.3);
  color: rgb(var(--blue-11));
}

.vp-vars {
  width: 60%;
  overflow-y: auto;
  padding: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.vp-var-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.1s ease;
  text-align: left;
  width: 100%;
}
.vp-var-item:hover {
  background: rgba(var(--blue-4), 0.15);
}
.vp-var-highlight {
  background: rgba(var(--amber-4), 0.12);
  border: 1px solid rgba(var(--amber-6), 0.5);
}
.vp-var-highlight:hover {
  background: rgba(var(--amber-4), 0.25);
}

.vp-var-type-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: rgba(var(--slate-4), 0.5);
  color: rgb(var(--slate-10));
  font-size: 10px;
  font-weight: 700;
  font-family: monospace;
  flex-shrink: 0;
}
.vp-badge-highlight {
  background: rgba(var(--amber-4), 0.6);
  color: rgb(var(--amber-11));
}

.vp-var-label {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vp-empty {
  text-align: center;
  padding: 1.5rem;
  font-size: 13px;
  color: rgb(var(--slate-9));
  line-height: 1.6;
}

/* Datasource list */
.vp-ds-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.25rem;
}

.vp-ds-hint {
  font-size: 12px;
  color: rgb(var(--slate-10));
  line-height: 1.5;
  padding: 0.25rem 0.375rem;
}

.vp-ds-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;
  width: 100%;
}
.vp-ds-item:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.vp-ds-name {
  font-size: 13px;
  font-weight: 600;
}

.vp-ds-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  gap: 0.25rem;
  text-align: center;
}

.vp-ds-manual {
  margin-top: auto;
  padding: 0.625rem;
  border-top: 1px solid rgb(var(--slate-4));
}

.vp-ds-manual-title {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--slate-10));
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.375rem;
}

.vp-ds-manual-row {
  display: flex;
  gap: 0.375rem;
  align-items: center;
}

.vp-ds-insert-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgb(var(--blue-9));
  color: white;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s ease;
}
.vp-ds-insert-btn:hover {
  background: rgb(var(--blue-10));
}

/* Datasource selected state */
.vp-ds-selected {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.625rem;
  width: 100%;
}

.vp-ds-selected-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vp-ds-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: rgb(var(--slate-10));
  transition: all 0.12s ease;
  cursor: pointer;
  flex-shrink: 0;
}
.vp-ds-back:hover {
  background: rgba(var(--alpha-2));
  color: rgb(var(--slate-12));
}

.vp-ds-selected-name {
  font-size: 14px;
  font-weight: 700;
}

.vp-field-label {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--slate-11));
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.vp-field-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 13px;
  color: rgb(var(--slate-12));
  background: rgb(var(--alpha-1));
  border: 1px solid rgb(var(--slate-5));
  border-radius: 8px;
  outline: none;
  font-family: monospace;
}
.vp-field-input:focus { border-color: rgb(var(--blue-9)); }

.vp-field-preview {
  padding: 0.5rem;
  background: rgba(var(--alpha-1));
  border-radius: 6px;
  font-size: 12px;
}
.vp-field-preview code {
  font-family: monospace;
  color: rgb(var(--blue-11));
  word-break: break-all;
}

.vp-field-insert {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: rgb(var(--blue-9));
  color: white;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s ease;
  cursor: pointer;
}
.vp-field-insert:hover { background: rgb(var(--blue-10)); }
.vp-field-insert:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
